import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI } from '../../api';

const InlinePaymentDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contactName,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    amount: 0,
    payment_method: 'credit_card',
    reference_number: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.amount || form.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      
      await contactsAPI.createPayment(contactId, {
        amount: form.amount,
        payment_method: form.payment_method,
        reference_number: form.reference_number,
        description: form.description,
        payment_date: new Date().toISOString(),
        status: 'completed'
      });
      
      toast.success('Payment recorded successfully');
      setForm({
        amount: 0,
        payment_method: 'credit_card',
        reference_number: '',
        description: ''
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({
      amount: 0,
      payment_method: 'credit_card',
      reference_number: '',
      description: ''
    });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-600" />
            Record Payment
          </DialogTitle>
          <DialogDescription>
            Record a payment from {contactName}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="payment-amount">Amount ($) *</Label>
            <Input
              id="payment-amount"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
              data-testid="inline-payment-amount"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm(prev => ({ ...prev, payment_method: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="credit_card">Credit Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Reference Number</Label>
            <Input
              value={form.reference_number}
              onChange={(e) => setForm(prev => ({ ...prev, reference_number: e.target.value }))}
              placeholder="Transaction ID or reference"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Payment description..."
              rows={2}
              data-testid="inline-payment-description"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || form.amount <= 0}>
              {loading ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

InlinePaymentDialog.displayName = 'InlinePaymentDialog';

export default InlinePaymentDialog;
